import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useForm } from 'react-hook-form'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { yupResolver } from '@hookform/resolvers/yup'
import { TextField, Banner } from '@dsny/dsny-component-library'
import Config from 'config'
import { useTranslation } from 'react-i18next'
import { loginSchema } from '../authSchema'
import {
  Container,
  FormWrapper,
  FullWidthButton,
  FooterText,
  Title,
  ForgotPasswordLabel,
  StyledCheckbox,
  Row,
  CheckboxLabel,
  HelpText,
  SignUpLink,
  PasswordWrapper
} from './AuthForm.styles'
import Tooltip from 'components/Tooltip'
import { AUTH_ERRORS } from 'constants/enums'

const LoginForm = ({ onSubmit, isSignInFailed, setIsMagicLinkFlow, error }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMobile = useMobileMediaQuery()
  const [showPassword, setShowPassword] = useState(false)
  const [publicDevice, setPublicDevice] = useState(false)
  const {
    setValue,
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(loginSchema)
  })

  const handleCheckBoxChange = () => {
    setValue('publicDevice', !publicDevice)
    setPublicDevice(!publicDevice)
  }

  const isSuccessSignUpCoworker = Boolean(
    searchParams.get('successSignUpCoworker')
  )

  return (
    <Container>
      <FormWrapper>
        {isSuccessSignUpCoworker && (
          <Banner
            mode="dark"
            style={{ marginBottom: 32, maxWidth: '100%' }}
            variant="info"
            bannerContent={{
              title: t('signup.account-created'),
              description: t('signup.account-created-body')
            }}
          />
        )}
        {error === AUTH_ERRORS.INVALID_CREDENTIALS && (
          <Banner
            mode="dark"
            style={{ marginBottom: 32, maxWidth: '100%' }}
            variant="error"
            bannerContent={{
              title: t('error.incorrect-credentials'),
              description: t('error.incorrect-credentials-message')
            }}
          />
        )}

        {error === AUTH_ERRORS.VERIFICATION_PENDING && (
          <Banner
            mode="dark"
            style={{ marginBottom: 32, maxWidth: '100%' }}
            variant="info"
            bannerContent={{
              title: t('error.verification-pending'),
              description: t('error.verification-pending-message')
            }}
          />
        )}
        <Title>{t('login.login-header-title')}</Title>

        <form
          data-cy="login-form"
          onSubmit={handleSubmit((values, e) => onSubmit(values, e))}
        >
          <div data-cy="login-email">
            <TextField
              id="email"
              name="email"
              required
              {...register('email')}
              setting="player"
              ref={null}
              inputRef={register('email').ref}
              wrapperStyles={{ paddingRight: 0, marginBottom: 32 }}
              labelText={t('signup.email')}
              error={errors?.email?.message || isSignInFailed}
              highlightColor={theme.colors.interact70}
              inputStyle={{
                height: 44,
                borderColor:
                  (errors?.email || isSignInFailed) && theme.colors.error70
              }}
              borderColor={theme.colors.neutralW50}
              errorStyle={{
                color: theme.colors.error70,
                marginTop: 8
              }}
            />
          </div>

          <PasswordWrapper data-cy="login-password">
            <TextField
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              {...register('password')}
              required
              setting="player"
              ref={null}
              inputRef={register('password').ref}
              wrapperStyles={{
                paddingRight: 0
              }}
              labelText={t('login.password')}
              error={errors?.password?.message || isSignInFailed}
              highlightColor={theme.colors.interact70}
              inputStyle={{
                height: 44,
                borderColor:
                  (errors?.password || isSignInFailed) && theme.colors.error70
              }}
              borderColor={theme.colors.neutralW50}
              errorStyle={{
                color: theme.colors.error70
              }}
              tailingIcon={showPassword ? 'Visibility' : 'VisibilityOff'}
              onClick={() => setShowPassword((prev) => !prev)}
              isTailingClickable
              tailingStyle={{
                marginRight: 8,
                transform: 'translateY(-50%)'
              }}
            />
          </PasswordWrapper>

          <Row
            style={{
              marginBottom: 24,
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              flexDirection: isMobile ? 'column' : 'row'
            }}
          >
            <Row>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <StyledCheckbox
                  id="publicDevice"
                  name="publicDevice"
                  {...register('publicDevice')}
                  checked={publicDevice}
                  onClick={handleCheckBoxChange}
                  style={{
                    marginRight: 3,
                    background: publicDevice
                      ? theme.colors.interact70
                      : theme.colors.neutralW20
                  }}
                />
                <CheckboxLabel htmlFor="publicDevice">
                  {t('login.public-computer')}
                </CheckboxLabel>
              </div>

              <Tooltip
                hasArrow={false}
                placement="bottom"
                text={t('login.public-computer-hint')}
                containerStyles={{
                  width: isMobile ? '70vw' : 407,
                  whiteSpace: 'normal',
                  padding: 0,
                  backgroundColor: theme.colors.neutralW90,
                  color: theme.colors.neutralW20,
                  margin: '6px 0'
                }}
                theme={theme}
                wrapperStyles={{
                  width: '100%',
                  display: 'flex',
                  flex: 1
                }}
              >
                <HelpText>{t('login.what-is-this')}</HelpText>
              </Tooltip>
            </Row>

            <Row style={{ margin: isMobile ? '16px 0 0' : '0 0 0 auto' }}>
              <ForgotPasswordLabel
                data-cy="login-forgot-password-link"
                onClick={() => {
                  navigate('/forgot-password')
                }}
              >
                {t('login.forgot-password')}
              </ForgotPasswordLabel>{' '}
            </Row>
          </Row>

          <FullWidthButton
            data-cy="login-submit"
            type="submit"
            color="primary"
            setting="player"
          >
            {t('login.login-cta')}
          </FullWidthButton>

          <FullWidthButton
            setting="player"
            variant="secondary"
            style={{ marginTop: 16 }}
            onClick={() => setIsMagicLinkFlow(true)}
          >
            {t('login.login-with-magic-link')}
          </FullWidthButton>
        </form>

        <FooterText>
          {t('login.dont-have-an-account')}
          <SignUpLink
            onClick={() => window.open(Config.endpoints.applyNow, '_blank')}
          >
            {t('signup.request-account')}
          </SignUpLink>
        </FooterText>
      </FormWrapper>
    </Container>
  )
}

LoginForm.propTypes = {
  isSignInFailed: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired
}

export default LoginForm
